// Three.js declares Mesh as `Mesh<any, any, any>` in its own type defs, so
// every traversal of a loaded scene flags no-unsafe-* even when the local
// code is correct. Scope-limited disable for the rules Three's typings break.
/* eslint-disable @typescript-eslint/no-unsafe-assignment -- Three.js types Mesh as Mesh<any,any,any>, flagging correct scene traversal; see header comment */
/* eslint-disable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-pass-data-to-parent, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- Three.js scene mutation is the canonical external-system effect; bounding-box compute is coupled to the traversal and must happen there. */
import { useEffect, useRef, useState, type RefObject } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { isNil } from "es-toolkit";
import type { MuscleId } from "@/entities/muscle/model/muscles";
import type { MuscleHighlightMap } from "@/entities/exercise/model/exercise-types";
import {
  MuscleClassifier,
  type MuscleClassification,
} from "@/entities/muscle/ui/muscle-classification.utils";
import {
  LimbRigging,
  type LimbPivots,
  type MeshUserData,
} from "@/entities/muscle/ui/limb-rigging.utils";
import { AnatomyScene as AnatomySceneUtils } from "@/entities/muscle/ui/anatomy-scene.utils";
import { classifyAnyDetailPart, DETAIL_PART_COLOR } from "@/entities/muscle/ui/biceps-parts";

export type AnatomySceneMeshes = {
  muscleMeshesRef: RefObject<Map<MuscleId, Array<THREE.Mesh>>>;
  genericMeshesRef: RefObject<Array<THREE.Mesh>>;
  partMeshesRef: RefObject<Map<string, Array<THREE.Mesh>>>;
  muscleCenters: Partial<Record<MuscleId, [number, number, number]>>;
};

const bucketMuscleMesh = (
  args: Readonly<{
    mesh: THREE.Mesh;
    muscleId: MuscleId;
    useClassifier: boolean;
    muscleMeshes: Map<MuscleId, Array<THREE.Mesh>>;
    partMeshes: Map<string, Array<THREE.Mesh>>;
  }>,
): void => {
  const { mesh, muscleId, useClassifier: shouldUseClassifier, muscleMeshes, partMeshes } = args;
  const bucket = muscleMeshes.get(muscleId) ?? [];
  bucket.push(mesh);
  muscleMeshes.set(muscleId, bucket);

  // A muscle mesh may also be a specific head (biceps long/short, etc.).
  if (!shouldUseClassifier) return;

  const part = classifyAnyDetailPart(MuscleClassifier.buildNameChain(mesh).join(" | "));
  if (isNil(part)) return;

  const partBucket = partMeshes.get(part) ?? [];
  partBucket.push(mesh);
  partMeshes.set(part, partBucket);
};

// Give each visible mesh its own material instance so highlight mutations
// don't leak across meshes that share the GLB's original material.
const ensureClonedMaterial = (mesh: THREE.Mesh, userData: MeshUserData): void => {
  if (isNil(userData.clonedMaterial) && !Array.isArray(mesh.material)) {
    mesh.material = mesh.material.clone();
    userData.clonedMaterial = mesh.material;
  }
};

const classifyMeshInto = (
  args: Readonly<{
    mesh: THREE.Mesh;
    useClassifier: boolean;
    muscleMeshes: Map<MuscleId, Array<THREE.Mesh>>;
    genericMeshes: Array<THREE.Mesh>;
    partMeshes: Map<string, Array<THREE.Mesh>>;
  }>,
): void => {
  const {
    mesh,
    useClassifier: shouldUseClassifier,
    muscleMeshes,
    genericMeshes,
    partMeshes,
  } = args;
  const classification: MuscleClassification = shouldUseClassifier
    ? MuscleClassifier.classifyMesh(mesh)
    : { kind: "generic" };
  const userData: MeshUserData = mesh.userData;
  userData.classification = classification;

  if (classification.kind === "hidden") {
    mesh.visible = false;

    return;
  }

  ensureClonedMaterial(mesh, userData);

  if (classification.kind === "muscle") {
    bucketMuscleMesh({
      mesh,
      muscleId: classification.muscleId,
      useClassifier: shouldUseClassifier,
      muscleMeshes,
      partMeshes,
    });
  } else {
    genericMeshes.push(mesh);
  }
};

const computeMuscleCenters = (
  clonedScene: THREE.Object3D,
  muscleMeshes: Map<MuscleId, Array<THREE.Mesh>>,
): Partial<Record<MuscleId, [number, number, number]>> => {
  const centers: Partial<Record<MuscleId, [number, number, number]>> = {};
  for (const [muscleId, meshes] of muscleMeshes) {
    const box = new THREE.Box3();
    let hasAny = false;
    for (const mesh of meshes) {
      const meshBox = new THREE.Box3().setFromObject(mesh);
      if (!meshBox.isEmpty()) {
        box.union(meshBox);
        hasAny = true;
      }
    }
    if (hasAny) {
      const center = new THREE.Vector3();
      box.getCenter(center);
      clonedScene.worldToLocal(center);
      centers[muscleId] = [center.x, center.y, center.z];
    }
  }

  return centers;
};

export const useAnatomySceneMeshes = ({
  clonedScene,
  useClassifier,
  pivotsRef,
}: {
  clonedScene: THREE.Object3D;
  useClassifier: boolean;
  pivotsRef: RefObject<LimbPivots>;
}): AnatomySceneMeshes => {
  const muscleMeshesRef = useRef<Map<MuscleId, Array<THREE.Mesh>>>(new Map());
  const genericMeshesRef = useRef<Array<THREE.Mesh>>([]);
  const partMeshesRef = useRef<Map<string, Array<THREE.Mesh>>>(new Map());
  const [muscleCenters, setMuscleCenters] = useState<
    Partial<Record<MuscleId, [number, number, number]>>
  >({});
  const invalidate = useThree((state) => state.invalidate);

  useEffect(() => {
    const muscleMeshes = new Map<MuscleId, Array<THREE.Mesh>>();
    const genericMeshes: Array<THREE.Mesh> = [];
    const partMeshes = new Map<string, Array<THREE.Mesh>>();

    clonedScene.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;

      classifyMeshInto({ mesh: object, useClassifier, muscleMeshes, genericMeshes, partMeshes });
    });

    muscleMeshesRef.current = muscleMeshes;
    genericMeshesRef.current = genericMeshes;
    partMeshesRef.current = partMeshes;

    if (useClassifier) {
      pivotsRef.current = LimbRigging.rig(clonedScene);
    }

    clonedScene.updateMatrixWorld(true);
    // oxlint-disable-next-line react/react-compiler -- centers are derived from Three.js scene traversal; computed in the same external-system effect as classification.
    setMuscleCenters(computeMuscleCenters(clonedScene, muscleMeshes));
    // frameloop is "demand": mesh visibility/material mutations above won't be
    // drawn until something requests a frame. Without this the canvas shows the
    // GLB's original (white) materials from the first frame until an interaction.
    invalidate();
  }, [clonedScene, useClassifier, pivotsRef, invalidate]);

  return { muscleMeshesRef, genericMeshesRef, partMeshesRef, muscleCenters };
};

const setHighlightTargets = (
  args: Readonly<{
    muscleMeshes: Map<MuscleId, Array<THREE.Mesh>>;
    genericMeshes: Array<THREE.Mesh>;
    partMeshes: Map<string, Array<THREE.Mesh>>;
    active: MuscleHighlightMap;
    activeHeads: ReadonlySet<string>;
  }>,
): void => {
  const { muscleMeshes, genericMeshes, partMeshes, active, activeHeads } = args;

  for (const [muscleId, meshes] of muscleMeshes) {
    const highlight = active[muscleId];
    for (const mesh of meshes)
      AnatomySceneUtils.setHighlightTarget({ mesh, highlight, isGeneric: false });
  }
  for (const mesh of genericMeshes) {
    AnatomySceneUtils.setHighlightTarget({ mesh, highlight: undefined, isGeneric: true });
  }
  // Paint detail-head accents on top of the coarse muscle highlight.
  for (const [part, meshes] of partMeshes) {
    if (!activeHeads.has(part)) continue;

    const colorOverride = DETAIL_PART_COLOR.get(part);
    if (isNil(colorOverride)) continue;

    for (const mesh of meshes)
      AnatomySceneUtils.setHighlightTarget({
        mesh,
        highlight: undefined,
        isGeneric: false,
        colorOverride,
      });
  }
};

// Per-frame color/opacity easing toward each mesh's target. Returns whether any
// mesh is still animating so the caller can keep requesting frames.
const stepAllHighlights = (
  muscleMeshesRef: RefObject<Map<MuscleId, Array<THREE.Mesh>>>,
  genericMeshesRef: RefObject<Array<THREE.Mesh>>,
  alpha: number,
): boolean => {
  let isAnimating = false;
  for (const meshes of muscleMeshesRef.current.values()) {
    for (const mesh of meshes) {
      if (AnatomySceneUtils.stepHighlight(mesh, alpha)) isAnimating = true;
    }
  }
  for (const mesh of genericMeshesRef.current) {
    if (AnatomySceneUtils.stepHighlight(mesh, alpha)) isAnimating = true;
  }

  return isAnimating;
};

type ApplyMuscleHighlightsArgs = {
  muscleMeshesRef: RefObject<Map<MuscleId, Array<THREE.Mesh>>>;
  genericMeshesRef: RefObject<Array<THREE.Mesh>>;
  partMeshesRef: RefObject<Map<string, Array<THREE.Mesh>>>;
  clonedScene: THREE.Object3D;
  active: MuscleHighlightMap;
  activeHeads: ReadonlySet<string>;
  onActiveBox: ((box: THREE.Box3 | null) => void) | undefined;
};

export const useApplyMuscleHighlights = ({
  muscleMeshesRef,
  genericMeshesRef,
  partMeshesRef,
  clonedScene,
  active,
  activeHeads,
  onActiveBox,
}: ApplyMuscleHighlightsArgs): void => {
  const invalidate = useThree((state) => state.invalidate);

  useEffect(() => {
    const muscleMeshes = muscleMeshesRef.current;

    setHighlightTargets({
      muscleMeshes,
      genericMeshes: genericMeshesRef.current,
      partMeshes: partMeshesRef.current,
      active,
      activeHeads,
    });

    // frameloop is "demand": kick a frame so the lerp in useFrame starts. It
    // self-sustains (re-invalidates) until every material reaches its target.
    invalidate();

    if (isNil(onActiveBox)) return;

    onActiveBox(AnatomySceneUtils.computePrimaryBoundingBox(muscleMeshes, active));
  }, [
    muscleMeshesRef,
    genericMeshesRef,
    partMeshesRef,
    clonedScene,
    active,
    activeHeads,
    onActiveBox,
    invalidate,
  ]);

  // Runs only while a frame is being drawn; keeps requesting frames until all
  // meshes settle, then goes quiet so the "demand" frameloop can idle.
  useFrame((_, delta) => {
    // Frame-rate-independent smoothing: ~0.18 blend at 60fps, capped so a long
    // delta (tab refocus) can't overshoot.
    const alpha = Math.min(1, delta * 11);
    if (stepAllHighlights(muscleMeshesRef, genericMeshesRef, alpha)) invalidate();
  });
};
